import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Download,
  FilterX,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { UserStatusBadge } from './UserStatusBadge';
import { OrganisationsFilterCombobox } from './OrganisationsFilterCombobox';
import { ImpersonateUserButton } from '@/shared/components/admin/ImpersonateUserButton';
import type {
  AdminLevel,
  PopulatedOrganisation,
  PopulatedSensorgroup,
  PopulatedUsergroup,
  User,
  UsersFilters,
} from '../types';

// =============================================================================
// CSV escape
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// =============================================================================
// Value helpers
// =============================================================================

function getAdminLevel(user: User, selectedOrgId?: string): string | null {
  if (!user.organisations || user.organisations.length === 0) return null;

  const orgProfile = selectedOrgId
    ? user.organisations.find((op) => {
        const orgId =
          typeof op.organisation === 'string'
            ? op.organisation
            : (op.organisation as PopulatedOrganisation)?._id;
        return orgId === selectedOrgId;
      })
    : user.organisations[0];

  const adminLevel = orgProfile?.admin_level;
  return typeof adminLevel === 'string' && adminLevel.trim() ? adminLevel : null;
}

function getOrganisationNames(user: User): string {
  if (!user.organisations || user.organisations.length === 0) return '';
  return user.organisations
    .map((op) => {
      if (!op.organisation) return null;
      if (typeof op.organisation === 'string') return op.organisation;
      return (op.organisation as PopulatedOrganisation)?.name || null;
    })
    .filter(Boolean)
    .join(', ');
}

function getUsergroupNames(user: User): string {
  if (user.usergroups && user.usergroups.length > 0) {
    return user.usergroups
      .map((ug) => (ug as PopulatedUsergroup)?.name)
      .filter(Boolean)
      .join(', ');
  }

  if (!user.organisations) return '';
  const all: string[] = [];
  for (const op of user.organisations) {
    if (op.usergroups) {
      for (const ug of op.usergroups) {
        if (typeof ug === 'string') continue;
        if ((ug as PopulatedUsergroup).name) {
          all.push((ug as PopulatedUsergroup).name);
        }
      }
    }
  }
  return all.join(', ');
}

function getSensorgroupNames(user: User): string {
  if (user.sensorgroups && user.sensorgroups.length > 0) {
    return user.sensorgroups
      .map((sg) => (sg as PopulatedSensorgroup)?.name)
      .filter(Boolean)
      .join(', ');
  }

  if (!user.organisations) return '';
  const all: string[] = [];
  for (const op of user.organisations) {
    if (op.sensorgroups) {
      for (const sg of op.sensorgroups) {
        if (typeof sg === 'string') continue;
        if ((sg as PopulatedSensorgroup).name) {
          all.push((sg as PopulatedSensorgroup).name);
        }
      }
    }
  }
  return all.join(', ');
}

function formatLastLogin(loginAt?: string): string {
  if (!loginAt) return 'Never';
  const date = new Date(loginAt);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return date.toLocaleDateString();
}

// =============================================================================
// AdminLevelBadge
// =============================================================================

const ADMIN_LEVEL_STYLES: Record<AdminLevel, { label: string; className: string }> = {
  'full admin': {
    label: 'Full Admin',
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
  },
  'group admin': {
    label: 'Group Admin',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  },
  'standard user': {
    label: 'Standard User',
    className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  },
  guest: {
    label: 'Guest',
    className: 'bg-orange-100 text-orange-800 hover:bg-orange-100',
  },
  auditor: {
    label: 'Auditor',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
};

function isAdminLevel(level: string): level is AdminLevel {
  return level in ADMIN_LEVEL_STYLES;
}

function formatAdminLevelLabel(level: string) {
  return level
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function AdminLevelBadge({ level }: { level: string | null }) {
  if (!level) return <span className="text-muted-foreground">—</span>;

  if (!isAdminLevel(level)) {
    return (
      <Badge variant="outline" className="text-muted-foreground" title={level}>
        {formatAdminLevelLabel(level)}
      </Badge>
    );
  }

  const variant = ADMIN_LEVEL_STYLES[level];
  return (
    <Badge variant="secondary" className={variant.className}>
      {variant.label}
    </Badge>
  );
}

// =============================================================================
// Column definition
// =============================================================================

interface FilterOption {
  label: string;
  value: string;
}

interface UserColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: UsersFilters['sortKey'];
  filterType?: 'text' | 'date-range' | 'select';
  filterOptions?: FilterOption[];
  getValue: (user: User) => string | number | null;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

// =============================================================================
// Column visibility persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-users-column-visibility';

function getDefaultVisibility(columns: UserColumnDef[]): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of columns) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(columns: UserColumnDef[]): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(columns), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility(columns);
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: UsersFilters['sortKey'];
  currentSortKey?: UsersFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: UsersFilters['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Props
// =============================================================================

interface UsersTableProps {
  users: User[];
  total: number;
  sortKey?: UsersFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort?: (key: UsersFilters['sortKey']) => void;
  onEditUser?: (user: User) => void;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  selectedOrgId?: string;
  onSelectedOrgChange?: (orgId: string) => void;
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  onExportAll?: () => void;
  isExportingAll?: boolean;
}

export function UsersTable({
  users,
  total,
  sortKey,
  sortDirection = 1,
  onSort,
  onEditUser,
  page = 0,
  pageSize = 25,
  onPageChange,
  selectedOrgId,
  onSelectedOrgChange,
  columnFilters,
  onColumnFilterChange,
  onClearColumnFilters,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  onExportAll,
  isExportingAll = false,
}: UsersTableProps) {
  const totalPages = Math.ceil(total / pageSize) || 1;

  // Column definitions depend on selectedOrgId (for admin-level lookup)
  const COLUMNS = useMemo<UserColumnDef[]>(
    () => [
      {
        key: 'user',
        label: 'User',
        defaultVisible: true,
        sortKey: 'lastname',
        filterType: 'text',
        getValue: (u) => {
          const name = [u.firstname, u.lastname].filter(Boolean).join(' ') || '(No name)';
          return `${name} ${u.email}`.trim();
        },
      },
      {
        key: 'firstname',
        label: 'First Name',
        defaultVisible: false,
        sortKey: 'firstname',
        filterType: 'text',
        getValue: (u) => u.firstname || null,
      },
      {
        key: 'lastname',
        label: 'Last Name',
        defaultVisible: false,
        sortKey: 'lastname',
        filterType: 'text',
        getValue: (u) => u.lastname || null,
      },
      {
        key: 'email',
        label: 'Email',
        defaultVisible: false,
        sortKey: 'email',
        filterType: 'text',
        getValue: (u) => u.email,
      },
      {
        key: 'organisations',
        label: 'Accounts',
        defaultVisible: true,
        filterType: 'text',
        getValue: (u) => getOrganisationNames(u) || null,
        cellClassName: 'max-w-[200px] truncate',
      },
      {
        key: 'adminLevel',
        label: 'Admin Level',
        defaultVisible: true,
        filterType: 'select',
        filterOptions: [
          { label: 'Full Admin', value: 'full admin' },
          { label: 'Group Admin', value: 'group admin' },
          { label: 'Standard User', value: 'standard user' },
          { label: 'Guest', value: 'guest' },
        ],
        getValue: (u) => getAdminLevel(u, selectedOrgId) || '',
      },
      {
        key: 'usergroups',
        label: 'User Groups',
        defaultVisible: true,
        filterType: 'text',
        getValue: (u) => getUsergroupNames(u) || null,
        cellClassName: 'max-w-[150px] truncate',
      },
      {
        key: 'sensorgroups',
        label: 'Sensor Groups',
        defaultVisible: true,
        filterType: 'text',
        getValue: (u) => getSensorgroupNames(u) || null,
        cellClassName: 'max-w-[150px] truncate',
      },
      {
        key: 'phone',
        label: 'Phone',
        defaultVisible: false,
        filterType: 'text',
        getValue: (u) => u.phone || null,
      },
      {
        key: 'phoneExtension',
        label: 'Phone Extension',
        defaultVisible: true,
        filterType: 'text',
        getValue: (u) => u.extension || null,
      },
      {
        key: 'mobile',
        label: 'Mobile',
        defaultVisible: false,
        filterType: 'text',
        getValue: (u) => u.mobile || null,
      },
      {
        key: 'company',
        label: 'Company',
        defaultVisible: false,
        filterType: 'text',
        getValue: (u) => u.company || null,
      },
      {
        key: 'position',
        label: 'Position',
        defaultVisible: false,
        filterType: 'text',
        getValue: (u) => u.position || null,
      },
      {
        key: 'language',
        label: 'Language',
        defaultVisible: false,
        filterType: 'select',
        filterOptions: [
          { label: 'English', value: 'en' },
          { label: 'French', value: 'fr' },
        ],
        getValue: (u) => u.language || null,
      },
      {
        key: 'lastLogin',
        label: 'Last Login',
        defaultVisible: true,
        sortKey: 'login_at',
        filterType: 'date-range',
        getValue: (u) => u.login_at || null,
        cellClassName: 'text-muted-foreground',
        headerClassName: 'w-32',
      },
      {
        key: 'createdAt',
        label: 'Created',
        defaultVisible: false,
        sortKey: 'created_at',
        filterType: 'date-range',
        getValue: (u) => u.created_at || null,
        cellClassName: 'text-muted-foreground',
        headerClassName: 'w-32',
      },
      {
        key: 'status',
        label: 'Status',
        defaultVisible: true,
        filterType: 'select',
        filterOptions: [
          { label: 'Enabled', value: 'enabled' },
          { label: 'Disabled', value: 'disabled' },
        ],
        getValue: (u) => (u.enabled ? 'enabled' : 'disabled'),
      },
      {
        key: 'actions',
        label: '',
        defaultVisible: true,
        noFilter: true,
        getValue: () => '',
        align: 'right',
        headerClassName: 'w-20',
      },
    ],
    [selectedOrgId],
  );

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility(COLUMNS),
  );

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [COLUMNS, columnVisibility],
  );

  // Client-side refinement on top of server-returned page
  const filteredUsers = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return users;

    return users.filter((user) =>
      activeFilters.every(([colKey, filterValue]) => {
        // Organisations filter is handled server-side via selectedOrgId
        if (colKey === 'organisations') return true;
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(user);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > `${to}T23:59:59`) return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [users, columnFilters, COLUMNS]);

  // Export handlers
  const handleCopy = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => c.label).join('\t');
    const rows = filteredUsers.map((u) =>
      exportCols.map((c) => String(c.getValue(u) ?? '')).join('\t'),
    );
    navigator.clipboard.writeText([header, ...rows].join('\n'));
    toast.success('Copied to clipboard');
  };

  const handleCSVPage = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
    const rows = filteredUsers.map((u) =>
      exportCols.map((c) => escapeCSVValue(c.getValue(u))).join(','),
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredUsers.length} rows`);
  };

  // Filter row cell renderer
  const renderColumnFilter = (col: UserColumnDef) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    if (col.key === 'organisations' && onSelectedOrgChange) {
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <OrganisationsFilterCombobox
            value={selectedOrgId}
            onChange={onSelectedOrgChange}
          />
        </TableHead>
      );
    }

    if (col.filterType === 'select' && col.filterOptions) {
      const selected: string[] = (() => {
        try {
          return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
        } catch {
          return [];
        }
      })();

      const toggle = (opt: string) => {
        const next = selected.includes(opt)
          ? selected.filter((o) => o !== opt)
          : [...selected, opt];
        onColumnFilterChange(col.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-full text-xs justify-start font-normal"
              >
                {selected.length ? `${selected.length} selected` : '—'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {col.filterOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                >
                  {opt.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    if (col.filterType === 'date-range') {
      const raw = columnFilters[col.key] || '';
      const [from = '', to = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={from}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${to}`)}
              title="From"
            />
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={to}
              onChange={(e) => onColumnFilterChange(col.key, `${from}>>${e.target.value}`)}
              title="To"
            />
          </div>
        </TableHead>
      );
    }

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // Cell renderer
  const renderCell = (col: UserColumnDef, user: User): React.ReactNode => {
    const raw = col.getValue(user);

    switch (col.key) {
      case 'user': {
        const fullName =
          [user.firstname, user.lastname].filter(Boolean).join(' ') || '(No name)';
        return (
          <div>
            <div className="font-medium">{fullName}</div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
          </div>
        );
      }
      case 'adminLevel':
        return <AdminLevelBadge level={getAdminLevel(user, selectedOrgId)} />;
      case 'status':
        return <UserStatusBadge enabled={user.enabled} />;
      case 'lastLogin':
        return formatLastLogin(user.login_at);
      case 'createdAt':
        return user.created_at ? new Date(user.created_at).toLocaleDateString() : '—';
      case 'language':
        if (user.language === 'fr') return 'French';
        if (user.language === 'en') return 'English';
        return '—';
      case 'actions':
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {onEditUser && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditUser(user);
                }}
                className="text-atek-blue hover:underline text-sm cursor-pointer"
              >
                Edit
              </button>
            )}
            <ImpersonateUserButton
              targetUserId={user._id}
              targetLabel={[user.firstname, user.lastname].filter(Boolean).join(' ') || user.email}
              iconOnly
              size="icon"
            />
          </div>
        );
      default:
        return String(raw ?? '') || '—';
    }
  };

  const hasActiveColumnFilters =
    Object.values(columnFilters).some(Boolean) ||
    (!!selectedOrgId && selectedOrgId !== 'all');

  const showingText =
    filteredUsers.length < users.length
      ? `Showing ${filteredUsers.length} of ${users.length} on this page`
      : `Showing ${users.length} of ${total}`;

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b flex-wrap gap-2">
        <span className="text-sm text-muted-foreground">{showingText}</span>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveColumnFilters && onClearColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExportingAll}>
                <Download className="h-4 w-4 mr-1" />
                {isExportingAll ? 'Exporting...' : 'CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCSVPage}>
                Export Page ({filteredUsers.length} rows)
              </DropdownMenuItem>
              {onExportAll && (
                <DropdownMenuItem onClick={() => onExportAll()}>
                  Export All ({total})
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn('h-4 w-4 mr-1', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={isVisible(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {/* ROW 1: Filter inputs */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col))}
            </TableRow>

            {/* ROW 2: Column labels + sort */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.headerClassName, col.align === 'right' && 'text-right')}
                >
                  {col.sortKey && onSort ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading &&
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!isLoading && filteredUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="py-12 text-center text-muted-foreground"
                >
                  No users found matching your filters.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              filteredUsers.map((user) => (
                <TableRow key={user._id} className="hover:bg-muted/50">
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.cellClassName,
                      )}
                    >
                      {renderCell(col, user)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
